import { activityKind } from "./enums.js";
import { dstMarkers, localDateKey, nextLocalDayStart, parseDateKey, startOfLocalDay, zonedEpoch, } from "./time.js";
export function emptyTotals() {
    return { driving: 0, work: 0, rest: 0, availability: 0, noCard: 0 };
}
/**
 * Totals for the overlap of one lane with `[fromMinute, toMinute)`.
 * Used to recalculate the summary/ribbon when the chart is zoomed.
 */
export function totalsInWindow(lane, fromMinute, toMinute, includeNoCard = true) {
    const totals = emptyTotals();
    if (!lane)
        return totals;
    const from = Math.max(0, fromMinute);
    const to = Math.max(from, toMinute);
    for (const segment of lane.segments) {
        if (!includeNoCard && segment.noCard)
            continue;
        const start = Math.max(segment.startMinute, from);
        const end = Math.min(segment.endMinute, to);
        if (end <= start)
            continue;
        // No-card time is its own bucket; it is not attributed to the underlying
        // activity in the totals (the segment keeps its kind for colouring only).
        if (segment.noCard)
            totals.noCard += end - start;
        else
            totals[segment.kind] += end - start;
    }
    return totals;
}
/**
 * Splits the visible time into normal and out-of-scope totals.
 * Out-of-scope ranges are in day minutes and are expected to be merged.
 */
export function totalsInWindowSplit(lane, fromMinute, toMinute, outOfScope = [], includeNoCard = true) {
    const normal = emptyTotals();
    const oos = emptyTotals();
    const noCardKinds = emptyTotals();
    if (!lane)
        return { normal, outOfScope: oos, noCardKinds };
    const from = Math.max(0, fromMinute);
    const to = Math.max(from, toMinute);
    for (const segment of lane.segments) {
        if (!includeNoCard && segment.noCard)
            continue;
        const start = Math.max(segment.startMinute, from);
        const end = Math.min(segment.endMinute, to);
        if (end <= start)
            continue;
        let oosMinutes = 0;
        for (const range of outOfScope) {
            const overlapStart = Math.max(start, range.startMinute);
            const overlapEnd = Math.min(end, range.endMinute);
            if (overlapEnd > overlapStart)
                oosMinutes += overlapEnd - overlapStart;
        }
        const normalMinutes = end - start - oosMinutes;
        const bucket = segment.noCard ? "noCard" : segment.kind;
        if (normalMinutes > 0)
            normal[bucket] += normalMinutes;
        if (oosMinutes > 0)
            oos[bucket] += oosMinutes;
        if (segment.noCard)
            noCardKinds[segment.kind] += end - start;
    }
    return { normal, outOfScope: oos, noCardKinds };
}
function addTotals(target, source) {
    target.driving += source.driving;
    target.work += source.work;
    target.rest += source.rest;
    target.availability += source.availability;
    target.noCard += source.noCard;
}
const NAME_KINDS = {
    DRIVING: "driving",
    WORK: "work",
    "BREAK/REST": "rest",
    BREAK: "rest",
    REST: "rest",
    AVAILABILITY: "availability",
};
function underlyingKind(typeName) {
    const name = String(typeName ?? "").trim().toUpperCase();
    return NAME_KINDS[name] ?? "noCard";
}
function cleanText(value) {
    if (value === null || value === undefined)
        return "";
    const trimmed = String(value).trim();
    if (trimmed === "-" || trimmed === "—")
        return "";
    return trimmed;
}
/** Splits `[start,end)` at local midnights in `timeZone`. */
export function splitByLocalMidnight(start, end, timeZone) {
    const pieces = [];
    let cursor = start;
    let guard = 0;
    while (cursor < end && guard < 400) {
        guard += 1;
        const nextMidnight = nextLocalDayStart(cursor, timeZone);
        const pieceEnd = nextMidnight > cursor ? Math.min(end, nextMidnight) : end;
        pieces.push({ start: cursor, end: pieceEnd });
        if (pieceEnd <= cursor)
            break;
        cursor = pieceEnd;
    }
    return pieces;
}
function resolveDayStart(input, timeZone) {
    if (input.date) {
        const parsed = parseDateKey(input.date);
        if (parsed) {
            return zonedEpoch(parsed.year, parsed.month, parsed.day, 0, 0, timeZone);
        }
    }
    return startOfLocalDay(input.day, timeZone);
}
function newLane(input) {
    return {
        label: input.label ?? "",
        driverName: cleanText(input.driverName),
        cardNumber: cleanText(input.cardNumber),
        segments: [],
        odoStart: typeof input.odoStart === "number" && input.odoStart > 0 ? input.odoStart : null,
        odoEnd: typeof input.odoEnd === "number" && input.odoEnd > 0 ? input.odoEnd : null,
        totals: emptyTotals(),
    };
}
function topUpLane(lane, input) {
    if (!lane.label && input.label)
        lane.label = input.label;
    if (!lane.driverName && input.driverName)
        lane.driverName = cleanText(input.driverName);
    if (!lane.cardNumber && input.cardNumber)
        lane.cardNumber = cleanText(input.cardNumber);
    if (lane.odoStart === null && typeof input.odoStart === "number" && input.odoStart > 0) {
        lane.odoStart = input.odoStart;
    }
    if (lane.odoEnd === null && typeof input.odoEnd === "number" && input.odoEnd > 0) {
        lane.odoEnd = input.odoEnd;
    }
}
function ingestLane(inputLane, sourceRow, byKey, timeZone, key) {
    if (!inputLane)
        return;
    let sourceLane = sourceRow[key];
    if (!sourceLane) {
        sourceLane = newLane(inputLane);
        sourceRow[key] = sourceLane;
    }
    else {
        topUpLane(sourceLane, inputLane);
    }
    for (const segment of inputLane.segments ?? []) {
        if (!segment || typeof segment.start !== "number" || typeof segment.end !== "number")
            continue;
        const start = Math.floor(segment.start);
        const end = Math.floor(segment.end);
        if (!(end > start))
            continue;
        for (const piece of splitByLocalMidnight(start, end, timeZone)) {
            const targetRow = byKey.get(localDateKey(piece.start, timeZone));
            if (!targetRow)
                continue;
            const durationMinutes = (piece.end - piece.start) / 60;
            if (durationMinutes <= 0)
                continue;
            let targetLane;
            if (targetRow === sourceRow) {
                targetLane = sourceLane;
            }
            else {
                const existing = targetRow[key];
                if (existing) {
                    targetLane = existing;
                    if (!targetLane.driverName)
                        targetLane.driverName = sourceLane.driverName;
                    if (!targetLane.cardNumber)
                        targetLane.cardNumber = sourceLane.cardNumber;
                }
                else {
                    targetLane = {
                        label: sourceLane.label,
                        driverName: sourceLane.driverName,
                        cardNumber: sourceLane.cardNumber,
                        segments: [],
                        odoStart: null,
                        odoEnd: null,
                        totals: emptyTotals(),
                    };
                    targetRow[key] = targetLane;
                }
            }
            const startMinute = Math.max(0, (piece.start - targetRow.dayStart) / 60);
            const endMinute = Math.min(targetRow.minutes, (piece.end - targetRow.dayStart) / 60);
            if (endMinute <= startMinute)
                continue;
            const entryType = String(segment.entryType ?? "");
            const baseKind = activityKind(segment.type);
            const noCard = baseKind === "noCard";
            targetLane.segments.push({
                id: "",
                source: segment,
                kind: noCard ? underlyingKind(segment.typeName) : baseKind,
                type: segment.type,
                typeName: segment.typeName,
                start: targetRow.dayStart + startMinute * 60,
                end: targetRow.dayStart + endMinute * 60,
                startMinute,
                endMinute,
                manual: /manual/i.test(entryType),
                noCard,
                driverName: cleanText(segment.driverName),
                cardNumber: cleanText(segment.cardNumber),
                entryType,
                drivingStatus: segment.drivingStatus,
            });
        }
    }
}
/**
 * Assigns the annotations of one day to the local day they belong to.
 *
 * Timestamps land on the day of their instant; ranges are split at local
 * midnight and clipped to each day; `none` entries stay on the source day.
 * Invalid timed entries degrade to list-only instead of disappearing.
 */
function ingestAnnotations(annotations, sourceRow, byKey, timeZone) {
    if (!Array.isArray(annotations))
        return;
    annotations.forEach((annotation, index) => {
        if (!annotation)
            return;
        const name = cleanText(annotation.name);
        if (!name)
            return;
        const base = {
            index,
            name,
            description: cleanText(annotation.description) || undefined,
            icon: annotation.icon,
            color: cleanText(annotation.color) || undefined,
        };
        const start = typeof annotation.start === "number" && Number.isFinite(annotation.start)
            ? Math.floor(annotation.start)
            : null;
        const end = typeof annotation.end === "number" && Number.isFinite(annotation.end)
            ? Math.floor(annotation.end)
            : null;
        if (annotation.type === "timestamp" && start !== null) {
            const target = byKey.get(localDateKey(start, timeZone));
            if (target) {
                const minute = (start - target.dayStart) / 60;
                if (minute >= 0 && minute <= target.minutes) {
                    target.annotations.push({ ...base, type: "timestamp", start, startMinute: minute });
                    return;
                }
            }
        }
        if (annotation.type === "range" && start !== null && end !== null && end > start) {
            let placed = false;
            for (const piece of splitByLocalMidnight(start, end, timeZone)) {
                const target = byKey.get(localDateKey(piece.start, timeZone));
                if (!target)
                    continue;
                const startMinute = Math.max(0, (piece.start - target.dayStart) / 60);
                const endMinute = Math.min(target.minutes, (piece.end - target.dayStart) / 60);
                if (endMinute <= startMinute)
                    continue;
                target.annotations.push({
                    ...base,
                    type: "range",
                    start,
                    end,
                    periodStart: start,
                    periodEnd: end,
                    startMinute,
                    endMinute,
                    continued: placed,
                });
                placed = true;
            }
            if (placed)
                return;
        }
        // `none`, or a timed entry whose times are incomplete/outside every row.
        sourceRow.annotations.push({ ...base, type: "none" });
    });
}
export function buildRenderModel(data, timeZone) {
    const rows = [];
    const byKey = new Map();
    const inputs = Array.isArray(data.days) ? data.days : [];
    // Pass 1: one row per local date, in chronological order.
    for (const input of inputs) {
        if (!input || typeof input.day !== "number" || !Number.isFinite(input.day))
            continue;
        const dayStart = resolveDayStart(input, timeZone);
        const dayEnd = nextLocalDayStart(dayStart, timeZone);
        const dateKey = localDateKey(dayStart, timeZone);
        const existing = byKey.get(dateKey);
        if (existing) {
            if (!existing.input.meta?.registrationNumber && input.meta?.registrationNumber) {
                existing.input.meta = { ...existing.input.meta, ...input.meta };
            }
            continue;
        }
        const row = {
            dateKey,
            dayStart,
            dayEnd,
            minutes: (dayEnd - dayStart) / 60,
            input,
            countries: [],
            countryEvents: [],
            vehicles: [],
            driverPeriods: [],
            coDriverPeriods: [],
            highlights: [],
            markers: [],
            annotations: [],
            outOfScope: [],
            ferry: [],
            dst: dstMarkers(dayStart, dayEnd, timeZone),
            totals: emptyTotals(),
        };
        rows.push(row);
        byKey.set(dateKey, row);
    }
    rows.sort((a, b) => a.dayStart - b.dayStart);
    // Pass 2a: country stays (top-level list, split at local midnight).
    for (const period of mergeCountryPeriods(data.countries ?? [])) {
        if (!period || typeof period.start !== "number" || typeof period.end !== "number")
            continue;
        const start = Math.floor(period.start);
        const end = Math.floor(period.end);
        if (!(end > start))
            continue;
        const country = cleanText(period.country);
        const countryCode = cleanText(period.countryCode) || undefined;
        for (const piece of splitByLocalMidnight(start, end, timeZone)) {
            const target = byKey.get(localDateKey(piece.start, timeZone));
            if (!target)
                continue;
            const startMinute = Math.max(0, (piece.start - target.dayStart) / 60);
            const endMinute = Math.min(target.minutes, (piece.end - target.dayStart) / 60);
            if (endMinute <= startMinute)
                continue;
            target.countries.push({
                country,
                countryCode,
                start: target.dayStart + startMinute * 60,
                end: target.dayStart + endMinute * 60,
                periodStart: start,
                periodEnd: end,
                startMinute,
                endMinute,
            });
        }
    }
    // Pass 2a1: country entry events (top-level list, one row each).
    for (const event of data.countryEvents ?? []) {
        if (!event || typeof event.time !== "number" || !Number.isFinite(event.time))
            continue;
        const country = cleanText(event.country);
        if (!country)
            continue;
        const time = Math.floor(event.time);
        const target = byKey.get(localDateKey(time, timeZone));
        if (!target)
            continue;
        const minute = (time - target.dayStart) / 60;
        if (minute < 0 || minute > target.minutes)
            continue;
        target.countryEvents.push({
            country,
            countryCode: cleanText(event.countryCode) || undefined,
            time,
            minute,
            epoch: time,
        });
    }
    // Pass 2a2: vehicle-use periods (top-level list, split at local midnight).
    for (const period of data.vehicles ?? []) {
        if (!period || typeof period.start !== "number" || typeof period.end !== "number")
            continue;
        const start = Math.floor(period.start);
        const end = Math.floor(period.end);
        if (!(end > start))
            continue;
        const registrationNumber = cleanText(period.registrationNumber);
        if (!registrationNumber)
            continue;
        const vehicleId = cleanText(period.vehicleId) || undefined;
        for (const piece of splitByLocalMidnight(start, end, timeZone)) {
            const target = byKey.get(localDateKey(piece.start, timeZone));
            if (!target)
                continue;
            const startMinute = Math.max(0, (piece.start - target.dayStart) / 60);
            const endMinute = Math.min(target.minutes, (piece.end - target.dayStart) / 60);
            if (endMinute <= startMinute)
                continue;
            target.vehicles.push({
                registrationNumber,
                vehicleId,
                start: target.dayStart + startMinute * 60,
                end: target.dayStart + endMinute * 60,
                periodStart: start,
                periodEnd: end,
                startMinute,
                endMinute,
            });
        }
    }
    // Pass 2a3: driver/co-driver use periods (top-level lists, split at local midnight).
    const ingestUsePeriods = (periods, pick) => {
        for (const period of periods ?? []) {
            if (!period || typeof period.start !== "number" || typeof period.end !== "number")
                continue;
            const start = Math.floor(period.start);
            const end = Math.floor(period.end);
            if (!(end > start))
                continue;
            const driverName = cleanText(period.driverName);
            if (!driverName)
                continue;
            const cardNumber = cleanText(period.cardNumber) || undefined;
            for (const piece of splitByLocalMidnight(start, end, timeZone)) {
                const target = byKey.get(localDateKey(piece.start, timeZone));
                if (!target)
                    continue;
                const startMinute = Math.max(0, (piece.start - target.dayStart) / 60);
                const endMinute = Math.min(target.minutes, (piece.end - target.dayStart) / 60);
                if (endMinute <= startMinute)
                    continue;
                pick(target).push({
                    driverName,
                    cardNumber,
                    start: target.dayStart + startMinute * 60,
                    end: target.dayStart + endMinute * 60,
                    periodStart: start,
                    periodEnd: end,
                    startMinute,
                    endMinute,
                });
            }
        }
    };
    ingestUsePeriods(data.driverPeriods, (day) => day.driverPeriods);
    ingestUsePeriods(data.coDriverPeriods, (day) => day.coDriverPeriods);
    // Pass 2a4: generic highlighted ranges (report bands, rest windows, no-data).
    for (const range of data.highlights ?? []) {
        if (!range || typeof range.start !== "number" || typeof range.end !== "number")
            continue;
        const start = Math.floor(range.start);
        const end = Math.floor(range.end);
        if (!(end > start))
            continue;
        const label = cleanText(range.label) || undefined;
        const color = cleanText(range.color) || undefined;
        for (const piece of splitByLocalMidnight(start, end, timeZone)) {
            const target = byKey.get(localDateKey(piece.start, timeZone));
            if (!target)
                continue;
            const startMinute = Math.max(0, (piece.start - target.dayStart) / 60);
            const endMinute = Math.min(target.minutes, (piece.end - target.dayStart) / 60);
            if (endMinute <= startMinute)
                continue;
            target.highlights.push({
                start: target.dayStart + startMinute * 60,
                end: target.dayStart + endMinute * 60,
                periodStart: start,
                periodEnd: end,
                startMinute,
                endMinute,
                label,
                color,
            });
        }
    }
    // Pass 2a5: generic vertical markers.
    for (const marker of data.markers ?? []) {
        if (!marker || typeof marker.time !== "number" || !Number.isFinite(marker.time))
            continue;
        const time = Math.floor(marker.time);
        const target = byKey.get(localDateKey(time, timeZone));
        if (!target)
            continue;
        const minute = (time - target.dayStart) / 60;
        if (minute < 0 || minute > target.minutes)
            continue;
        target.markers.push({
            time,
            minute,
            label: cleanText(marker.label) || undefined,
            color: cleanText(marker.color) || undefined,
            dashed: marker.dashed === true,
        });
    }
    // Pass 2b: the single driver lane.
    for (const input of inputs) {
        if (!input || typeof input.day !== "number" || !Number.isFinite(input.day))
            continue;
        const sourceRow = byKey.get(localDateKey(resolveDayStart(input, timeZone), timeZone));
        if (!sourceRow)
            continue;
        for (const range of input.outOfScope ?? []) {
            if (!range || !(range.end > range.start))
                continue;
            for (const piece of splitByLocalMidnight(range.start, range.end, timeZone)) {
                const target = byKey.get(localDateKey(piece.start, timeZone));
                if (!target)
                    continue;
                const startMinute = Math.max(0, (piece.start - target.dayStart) / 60);
                const endMinute = Math.min(target.minutes, (piece.end - target.dayStart) / 60);
                if (endMinute > startMinute)
                    target.outOfScope.push({ startMinute, endMinute });
            }
        }
        for (const instant of input.ferry ?? []) {
            if (typeof instant !== "number" || !Number.isFinite(instant))
                continue;
            const target = byKey.get(localDateKey(instant, timeZone));
            if (!target)
                continue;
            const minute = (instant - target.dayStart) / 60;
            if (minute < 0 || minute > target.minutes)
                continue;
            target.ferry.push({ minute, epoch: instant });
        }
        ingestLane(input.lane, sourceRow, byKey, timeZone, "lane");
        ingestLane(input.coDriverLane, sourceRow, byKey, timeZone, "coDriverLane");
        ingestAnnotations(input.annotations, sourceRow, byKey, timeZone);
    }
    // Pass 3: finalise each row.
    const hasAnyActivityLane = rows.some((row) => !!row.lane && row.lane.segments.length > 0);
    const hasAnyCoDriverLane = rows.some((row) => !!row.coDriverLane && row.coDriverLane.segments.length > 0);
    for (const row of rows) {
        row.outOfScope = mergeRanges(row.outOfScope).slice(0, 500);
        row.ferry = mergeFerry(row.ferry);
        row.countries = finaliseCountries(row);
        row.countryEvents = finaliseCountryEvents(row);
        row.vehicles = finaliseVehicles(row);
        row.driverPeriods = finaliseDriverPeriods(row, "driverPeriods", hasAnyActivityLane);
        row.coDriverPeriods = finaliseDriverPeriods(row, "coDriverPeriods", hasAnyCoDriverLane);
        row.highlights.sort((a, b) => a.startMinute - b.startMinute);
        row.markers = finaliseMarkers(row);
        if (row.coDriverLane) {
            finaliseLane(row, row.coDriverLane, 'codriver');
        }
        if (row.lane) {
            finaliseLane(row, row.lane);
            addTotals(row.totals, row.lane.totals);
        }
    }
    const offsets = new Map();
    let cursor = 0;
    for (const row of rows) {
        offsets.set(row.dateKey, cursor);
        cursor += row.minutes;
    }
    return { days: rows, minutesTotal: cursor, offsets };
}
function mergeRanges(ranges) {
    if (ranges.length === 0)
        return [];
    const sorted = [...ranges].sort((a, b) => a.startMinute - b.startMinute);
    const merged = [];
    for (const range of sorted) {
        const last = merged[merged.length - 1];
        if (last && range.startMinute <= last.endMinute + 0.5) {
            last.endMinute = Math.max(last.endMinute, range.endMinute);
        }
        else {
            merged.push({ ...range });
        }
    }
    return merged;
}
function mergeFerry(markers) {
    const seen = new Set();
    const out = [];
    for (const marker of markers.sort((a, b) => a.minute - b.minute)) {
        const key = Math.round(marker.minute * 10) / 10;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(marker);
    }
    return out;
}
/**
 * Contiguous pieces of the same country are one stay. Consumers often build
 * one period per day bucket; merging those before the per-day split keeps the
 * tooltip total equal to the whole stay instead of the first day piece.
 */
function mergeCountryPeriods(periods) {
    const valid = periods
        .filter((period) => period && typeof period.start === "number" && typeof period.end === "number")
        .filter((period) => period.end > period.start)
        .map((period) => ({ ...period }))
        .sort((a, b) => a.start - b.start);
    const out = [];
    for (const period of valid) {
        const previous = out[out.length - 1];
        if (previous &&
            previous.country === period.country &&
            previous.countryCode === period.countryCode &&
            period.start <= previous.end + 1) {
            previous.end = Math.max(previous.end, period.end);
        }
        else {
            out.push(period);
        }
    }
    return out;
}
function finaliseCountries(row) {
    if (row.countries.length === 0)
        return [];
    const sorted = [...row.countries].sort((a, b) => a.startMinute - b.startMinute);
    const out = [];
    let cursor = 0;
    for (const range of sorted) {
        if (range.startMinute < cursor)
            range.startMinute = cursor;
        if (range.endMinute > row.minutes)
            range.endMinute = row.minutes;
        if (range.endMinute <= range.startMinute)
            continue;
        const previous = out[out.length - 1];
        if (previous &&
            previous.country === range.country &&
            previous.countryCode === range.countryCode &&
            Math.abs(previous.endMinute - range.startMinute) < 0.01) {
            previous.endMinute = range.endMinute;
            previous.end = row.dayStart + range.endMinute * 60;
            previous.periodEnd = range.periodEnd;
        }
        else {
            range.start = row.dayStart + range.startMinute * 60;
            range.end = row.dayStart + range.endMinute * 60;
            out.push(range);
        }
        cursor = range.endMinute;
    }
    return out;
}
function finaliseCountryEvents(row) {
    if (row.countryEvents.length === 0)
        return [];
    const byMinute = new Map();
    for (const event of row.countryEvents) {
        // Two entries at the same instant describe the same border crossing; the
        // last event is the country that is in effect afterwards.
        byMinute.set(Math.round(event.minute * 10) / 10, event);
    }
    return [...byMinute.values()].sort((a, b) => a.minute - b.minute);
}
function finaliseVehicles(row) {
    if (row.vehicles.length === 0)
        return [];
    const sorted = [...row.vehicles].sort((a, b) => a.startMinute - b.startMinute);
    const out = [];
    let cursor = 0;
    for (const range of sorted) {
        if (range.startMinute < cursor)
            range.startMinute = cursor;
        if (range.endMinute > row.minutes)
            range.endMinute = row.minutes;
        if (range.endMinute <= range.startMinute)
            continue;
        const previous = out[out.length - 1];
        if (previous &&
            previous.registrationNumber === range.registrationNumber &&
            previous.vehicleId === range.vehicleId &&
            Math.abs(previous.endMinute - range.startMinute) < 0.01) {
            previous.endMinute = range.endMinute;
            previous.end = row.dayStart + range.endMinute * 60;
            previous.periodEnd = range.periodEnd;
        }
        else {
            range.start = row.dayStart + range.startMinute * 60;
            range.end = row.dayStart + range.endMinute * 60;
            out.push(range);
        }
        cursor = range.endMinute;
    }
    return out;
}
function finaliseMarkers(row) {
    if (row.markers.length === 0)
        return [];
    const byMinute = new Map();
    for (const marker of row.markers) {
        byMinute.set(Math.round(marker.minute * 10) / 10, marker);
    }
    return [...byMinute.values()].sort((a, b) => a.minute - b.minute);
}
/**
 * Driver bands come from the roster (card insertion/extraction), which can
 * disagree with the activity lane: a card can be reported as inserted while the
 * activities of that stretch are no-card ("Card withdrawal"). The activities
 * have priority, so a band is clipped to the driver's card activities and split
 * around no-card gaps.
 */
function finaliseDriverPeriods(row, key, hasAnyLane) {
    const source = row[key];
    if (source.length === 0)
        return [];
    const lane = key === "coDriverPeriods" ? row.coDriverLane : row.lane;
    const pieces = [];
    for (const range of source) {
        const startMinute = Math.max(0, range.startMinute);
        const endMinute = Math.min(row.minutes, range.endMinute);
        if (endMinute <= startMinute)
            continue;
        // A day without activities inside a chart that has activities elsewhere is
        // a no-data day: the roster must not stretch the band across it. Only a
        // roster-only chart (no lane anywhere) keeps the band as provided.
        if (!lane || lane.segments.length === 0) {
            if (hasAnyLane)
                continue;
            pieces.push({ ...range, startMinute, endMinute });
            continue;
        }
        const cardRanges = cardActivityRanges(lane, range);
        if (!cardRanges) {
            pieces.push({ ...range, startMinute, endMinute });
            continue;
        }
        for (const card of cardRanges) {
            const start = Math.max(startMinute, card.startMinute);
            const end = Math.min(endMinute, card.endMinute);
            if (end > start) {
                pieces.push({
                    ...range,
                    startMinute: start,
                    endMinute: end,
                    // The card-visible piece is the stay now; otherwise the band would
                    // claim a continuation that the activities contradict.
                    periodStart: row.dayStart + start * 60,
                    periodEnd: row.dayStart + end * 60,
                });
            }
        }
    }
    pieces.sort((a, b) => a.startMinute - b.startMinute);
    const out = [];
    let cursor = 0;
    for (const range of pieces) {
        if (range.startMinute < cursor)
            range.startMinute = cursor;
        if (range.endMinute > row.minutes)
            range.endMinute = row.minutes;
        if (range.endMinute <= range.startMinute)
            continue;
        const previous = out[out.length - 1];
        if (previous &&
            previous.driverName === range.driverName &&
            previous.cardNumber === range.cardNumber &&
            Math.abs(previous.endMinute - range.startMinute) < 0.01) {
            previous.endMinute = range.endMinute;
            previous.end = row.dayStart + range.endMinute * 60;
            previous.periodEnd = range.periodEnd;
        }
        else {
            range.start = row.dayStart + range.startMinute * 60;
            range.end = row.dayStart + range.endMinute * 60;
            out.push(range);
        }
        cursor = range.endMinute;
    }
    return out;
}
/**
 * Card-attributed minutes of the same driver, or `null` when the lane has no
 * activity data at all (in that case the roster band is kept as-is).
 */
function cardActivityRanges(lane, period) {
    if (!lane || lane.segments.length === 0)
        return null;
    // Card numbers are stable; names are not (the report often only has a
    // display name), so only the card number narrows the match. Masked numbers
    // ("***123") are not usable and fall back to the whole lane.
    const card = cleanText(period.cardNumber);
    const matchByCard = !!card && !card.includes("*");
    const ranges = [];
    for (const segment of lane.segments) {
        if (segment.noCard)
            continue;
        if (matchByCard && segment.cardNumber && segment.cardNumber !== card)
            continue;
        ranges.push({ startMinute: segment.startMinute, endMinute: segment.endMinute });
    }
    return mergeRanges(ranges);
}
function finaliseLane(row, lane, prefix = '') {
    const sorted = [...lane.segments].sort((a, b) => a.startMinute - b.startMinute || b.endMinute - a.endMinute);
    const result = [];
    let cursor = 0;
    for (const segment of sorted) {
        if (segment.startMinute < cursor)
            segment.startMinute = cursor;
        if (segment.endMinute > row.minutes)
            segment.endMinute = row.minutes;
        if (segment.endMinute <= segment.startMinute)
            continue;
        segment.start = row.dayStart + segment.startMinute * 60;
        segment.end = row.dayStart + segment.endMinute * 60;
        segment.id = `${row.dateKey}|${prefix}${result.length}`;
        result.push(segment);
        cursor = segment.endMinute;
    }
    lane.segments = result;
    lane.totals = emptyTotals();
    for (const segment of result) {
        lane.totals[segment.noCard ? "noCard" : segment.kind] += segment.endMinute - segment.startMinute;
    }
    if (!lane.driverName) {
        const named = result.find((segment) => segment.driverName);
        lane.driverName = named?.driverName ?? "";
    }
    if (!lane.cardNumber) {
        const carded = result.find((segment) => segment.cardNumber);
        lane.cardNumber = carded?.cardNumber ?? "";
    }
}
